import { TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { computeReitMetrics } from '@/lib/utils/utils.reits.ts';
import {ReitHolding} from "@/@types/reit.models.ts";

interface REITMetricsCardsProps {
  holding: ReitHolding;
  totalDividendsReceived: number;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function REITMetricsCards({ holding, totalDividendsReceived }: REITMetricsCardsProps) {
  const { marketValue, costBasis, unrealizedGainLoss, unrealizedGainLossPct, annualIncome } =
    computeReitMetrics(holding);

  const isGain = unrealizedGainLoss >= 0;

  const dividendYield =
    holding.current_price_per_unit === 0
      ? null
      : (holding.dividend_per_unit / holding.current_price_per_unit) * 100;

  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 gap-4"
      role="region"
      aria-label="REIT performance metrics"
    >
      {/* Market Value */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Market Value</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatCurrency(marketValue, holding.currency)}
          </div>
        </CardContent>
      </Card>

      {/* Cost Basis */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Cost Basis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatCurrency(costBasis, holding.currency)}
          </div>
        </CardContent>
      </Card>

      {/* Unrealized G/L */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Unrealized G/L</CardTitle>
          {isGain ? (
            <TrendingUp className="h-4 w-4 text-emerald-600" aria-hidden="true" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-600" aria-hidden="true" />
          )}
        </CardHeader>
        <CardContent>
          <div
            className={`text-2xl font-bold ${isGain ? 'text-emerald-600' : 'text-red-600'}`}
            data-testid="unrealized-gl"
          >
            {formatCurrency(unrealizedGainLoss, holding.currency)}
            <span className="text-sm font-normal ml-1">
              ({unrealizedGainLossPct.toFixed(2)}%)
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Annual Income */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Annual Income</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatCurrency(annualIncome, holding.currency)}
          </div>
        </CardContent>
      </Card>

      {/* Dividend Yield */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Dividend Yield</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {dividendYield === null ? 'N/A' : `${dividendYield.toFixed(2)}%`}
          </div>
        </CardContent>
      </Card>

      {/* Total Dividends Received */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total Dividends Received
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatCurrency(totalDividendsReceived, holding.currency)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
